import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  LinearProgress,
  Paper,
  Typography,
} from "@mui/material";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import ClientAutocomplete from "../../components/clientAutocomplete";
import ExceptionsReport from "../../components/exceptions-report-dialog/report";
import AnalysisPageTopbar from "../../components/metrics/AnalysisPageTopbar";
import SalesPerformancePageLoading from "../../components/metrics/SalesPerformancePageLoading";
import {
  PAGE_FONT,
  useMetricsUi,
} from "../../components/metrics/metricsUi";
import { isMobile } from "../../globalStyles/mobile";
import { fieldSx } from "../manage-catalog/constants";
import {
  buildDefaultExceptionRange,
  buildExceptionsRequestBody,
  getDisplayTimeZone,
  getExceptionsClientId,
  getExceptionsGenerateError,
  isInvalidExceptionRange,
  resolveDefaultExceptionStatuses,
} from "../../utils/orderExceptionsDefaults";
import { orderExceptionsService } from "../../utils/services/orderExceptionsService";

const LateShipPage = () => {
  const ui = useMetricsUi();
  const { colors, darkMode } = ui;
  const storedClientId = getExceptionsClientId();
  const [selectedClient, setSelectedClient] = useState(null);
  const clientId = selectedClient?._id || storedClientId;

  const [range, setRange] = useState(() => buildDefaultExceptionRange(dayjs));
  const [statuses, setStatuses] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(() => Boolean(getExceptionsClientId()));
  const [error, setError] = useState("");
  const [filtersResetKey, setFiltersResetKey] = useState(0);
  const exportActionsRef = useRef(null);
  const rangeRef = useRef(range);
  const statusesRef = useRef(statuses);
  const selectedClientRef = useRef(selectedClient);

  rangeRef.current = range;
  statusesRef.current = statuses;
  selectedClientRef.current = selectedClient;

  const { start, end } = range;
  const invalidRange = isInvalidExceptionRange(start, end);
  const mobile = isMobile();

  const fetchReport = useCallback(async () => {
    const id = selectedClientRef.current?._id || getExceptionsClientId();
    if (!id) return;

    const { start: nextStart, end: nextEnd } = rangeRef.current;
    if (isInvalidExceptionRange(nextStart, nextEnd)) return;

    setLoading(true);
    setError("");
    try {
      let resolved = statusesRef.current;
      if (!resolved) {
        try {
          const options = await orderExceptionsService.options();
          resolved = resolveDefaultExceptionStatuses(options);
        } catch {
          resolved = resolveDefaultExceptionStatuses();
        }
        setStatuses(resolved);
      }
      const reportData = await orderExceptionsService.generate({
        ...buildExceptionsRequestBody({
          statuses: resolved,
          start: nextStart,
          end: nextEnd,
        }),
        client_id: id,
      });
      setReport(reportData);
    } catch (err) {
      setError(getExceptionsGenerateError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!clientId) {
      setLoading(false);
      setReport(null);
      setError("");
      return undefined;
    }
    fetchReport();
    return undefined;
  }, [clientId, fetchReport]);

  const handleReset = () => {
    setRange(buildDefaultExceptionRange(dayjs));
    setFiltersResetKey((current) => current + 1);
  };

  const handleDownloadHtml = () => {
    exportActionsRef.current?.downloadHtml?.();
  };

  const handleDownloadExcel = () => {
    exportActionsRef.current?.downloadExcel?.();
  };

  const cardSx = ui.card;

  return (
    <Box
      sx={{
        backgroundColor: darkMode ? colors.bgPrimary : "#F5F6F8",
        minHeight: "100%",
        fontFamily: PAGE_FONT,
        position: "relative",
      }}
    >
      {loading && (
        <LinearProgress
          sx={{ position: "absolute", top: 0, left: 0, right: 0, zIndex: 1 }}
        />
      )}
      <Box
        sx={{
          maxWidth: 1480,
          mx: "auto",
          padding: mobile ? "14px 12px 40px" : "22px 28px 56px",
        }}
      >
        <AnalysisPageTopbar
          badge="OE"
          title="Order Exceptions"
          clientName={selectedClient?.business_name || selectedClient?.name}
          right={
            !storedClientId ? (
              <ClientAutocomplete
                width={220}
                selectedClient={selectedClient}
                onClientChange={setSelectedClient}
              />
            ) : null
          }
        />

        {!clientId ? (
          <Alert severity="info" sx={{ mb: 2, borderRadius: "10px" }}>
            Select a client to view unfulfilled orders and inventory
            availability.
          </Alert>
        ) : (
          <>
            <Paper elevation={0} sx={{ ...cardSx, p: mobile ? 2 : 2.5, mb: 2 }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1.5,
                    alignItems: mobile ? "stretch" : "flex-end",
                    flexDirection: mobile ? "column" : "row",
                  }}
                >
                  <Box sx={{ minWidth: mobile ? "100%" : 250, flex: "1 1 240px", maxWidth: mobile ? "100%" : 320 }}>
                    <DateTimePicker
                      label="Start date/time"
                      value={start}
                      onChange={(value) =>
                        setRange((current) => ({ ...current, start: value }))
                      }
                      disabled={loading}
                      ampm={false}
                      slotProps={{
                        textField: { fullWidth: true, size: "small", sx: fieldSx },
                      }}
                    />
                  </Box>
                  <Box sx={{ minWidth: mobile ? "100%" : 250, flex: "1 1 240px", maxWidth: mobile ? "100%" : 320 }}>
                    <DateTimePicker
                      label="End date/time"
                      value={end}
                      onChange={(value) =>
                        setRange((current) => ({ ...current, end: value }))
                      }
                      disabled={loading}
                      ampm={false}
                      slotProps={{
                        textField: { fullWidth: true, size: "small", sx: fieldSx },
                      }}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 1,
                      alignItems: "center",
                    }}
                  >
                    <Button
                      variant="contained"
                      disabled={loading || invalidRange}
                      onClick={fetchReport}
                      sx={{ textTransform: "none", fontFamily: PAGE_FONT }}
                    >
                      Apply
                    </Button>
                    <Button
                      disabled={loading}
                      onClick={handleReset}
                      sx={{ textTransform: "none", fontFamily: PAGE_FONT }}
                    >
                      Reset
                    </Button>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 1,
                      ml: mobile ? 0 : "auto",
                    }}
                  >
                    <Button
                      onClick={handleDownloadHtml}
                      disabled={loading || !report || report.empty}
                      sx={{ textTransform: "none", fontFamily: PAGE_FONT }}
                    >
                      Download HTML
                    </Button>
                    <Button
                      onClick={handleDownloadExcel}
                      disabled={loading || !report || report.empty}
                      sx={{ textTransform: "none", fontFamily: PAGE_FONT }}
                    >
                      Download Excel
                    </Button>
                  </Box>
                </Box>
              </LocalizationProvider>
              <Typography
                variant="caption"
                sx={{ display: "block", mt: 1.5, color: colors.textSecondary }}
              >
                Times are in {getDisplayTimeZone()}.
              </Typography>
            </Paper>

            {loading && !report && (
              <SalesPerformancePageLoading
                title="Loading order exceptions..."
                kpiCount={4}
              />
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 2, borderRadius: "10px" }}>
                {error}
              </Alert>
            )}

            {report && (
              <Paper elevation={0} sx={{ ...cardSx, p: mobile ? 1.5 : 2.5 }}>
                <ExceptionsReport
                  report={report}
                  layout="page"
                  exportActionsRef={exportActionsRef}
                  filtersResetKey={filtersResetKey}
                />
              </Paper>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default LateShipPage;
